import React, { useEffect, useState, useCallback } from 'react';
import { fmtWh } from './time';
import { authFetch, API } from './api';

// Information → Motley Denim → Shipping
// Reference view of ALL WGR (Wikinggruppen) shipping methods — including
// deleted ones. 16 years of shop history; kept read-only for reference.

interface WgrMethod {
  method_id: number;
  title: string;
  unifaun_code: string;
  zone: string;
  is_deleted: boolean;
  data: Record<string, any>;
  fetched_at: string;
}

export function WgrShippingInfo() {
  const [methods, setMethods] = useState<WgrMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [search, setSearch] = useState('');
  const [expanded, setExpanded] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    const res = await authFetch(`${API}/information/wgr-shipping-methods`);
    if (res.ok) {
      const data = await res.json();
      setMethods(data.methods || []);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    setRefreshing(true);
    setError(null);
    try {
      const res = await authFetch(`${API}/information/wgr-shipping-methods/refresh`, {
        method: 'POST', body: JSON.stringify({}),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || 'Refresh failed');
      } else {
        await load();
      }
    } catch {
      setError('Refresh failed — check server logs');
    }
    setRefreshing(false);
  };

  const term = search.trim().toLowerCase();
  const visible = methods.filter(m =>
    (showDeleted || !m.is_deleted) &&
    (!term ||
      m.title.toLowerCase().includes(term) ||
      (m.unifaun_code || '').toLowerCase().includes(term) ||
      (m.zone || '').toLowerCase().includes(term) ||
      String(m.method_id).includes(term))
  );
  const activeCount = methods.filter(m => !m.is_deleted).length;
  const fetchedAt = methods[0]?.fetched_at || '';

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-1 flex-wrap gap-3">
        <h1 className="text-white text-lg font-semibold">Motley Denim — Shipping methods (WGR)</h1>
        <button onClick={refresh} disabled={refreshing}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
          {refreshing ? 'Refreshing…' : 'Refresh from WGR'}
        </button>
      </div>
      <p className="text-gray-500 text-sm mb-4">
        Reference copy of every shipping method configured in the webshop over the years —
        {` ${activeCount} active, ${methods.length - activeCount} deleted.`}
        {fetchedAt && ` Last fetched ${fmtWh(fetchedAt, 'short')}.`}
        {' '}The nShift code column is what the "To nShift" flow uses today.
      </p>
      {error && <p className="text-red-400 text-sm mb-3">{error}</p>}

      <div className="flex items-center gap-4 mb-4 flex-wrap">
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search name, zone, code, id…"
          className="w-72 bg-gray-800 border border-gray-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
        />
        <label className="flex items-center gap-2 text-gray-400 text-sm cursor-pointer select-none">
          <input type="checkbox" checked={showDeleted} onChange={e => setShowDeleted(e.target.checked)} />
          Show deleted ({methods.length - activeCount})
        </label>
        <span className="text-gray-600 text-sm">{visible.length} shown</span>
      </div>

      {loading ? (
        <div className="text-gray-500 text-sm py-8">Loading…</div>
      ) : methods.length === 0 ? (
        <div className="text-gray-500 text-sm py-8">
          Nothing imported yet — click "Refresh from WGR" to fetch the shipping method list.
        </div>
      ) : (
        <div className="bg-[#1a1d27] rounded-xl border border-gray-800 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800">
                <th className="text-left px-4 py-3">ID</th>
                <th className="text-left px-4 py-3">Name (sv)</th>
                <th className="text-left px-4 py-3">Name (en)</th>
                <th className="text-left px-4 py-3">Zone</th>
                <th className="text-left px-4 py-3">nShift code</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody>
              {visible.map(m => (
                <React.Fragment key={m.method_id}>
                  <tr
                    onClick={() => setExpanded(expanded === m.method_id ? null : m.method_id)}
                    className={`border-b border-gray-800/60 cursor-pointer hover:bg-gray-800/40 ${m.is_deleted ? 'opacity-50' : ''}`}
                  >
                    <td className="px-4 py-2.5 text-gray-500">{m.method_id}</td>
                    <td className="px-4 py-2.5 text-white">{m.title || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-400">{m.data?.title_en || '—'}</td>
                    <td className="px-4 py-2.5 text-gray-400">{m.zone || '—'}</td>
                    <td className="px-4 py-2.5">
                      {m.unifaun_code
                        ? <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-blue-300 font-mono">{m.unifaun_code}</span>
                        : <span className="text-gray-600">—</span>}
                    </td>
                    <td className="px-4 py-2.5">
                      {m.is_deleted
                        ? <span className="text-xs px-2 py-0.5 rounded bg-gray-800 text-gray-500">Deleted</span>
                        : <span className="text-xs px-2 py-0.5 rounded bg-green-900/50 text-green-400">Active</span>}
                    </td>
                  </tr>
                  {expanded === m.method_id && (
                    <tr className="border-b border-gray-800/60 bg-gray-900/40">
                      <td colSpan={6} className="px-4 py-3">
                        <pre className="text-xs text-gray-400 whitespace-pre-wrap max-h-96 overflow-auto">
                          {JSON.stringify(m.data, null, 2)}
                        </pre>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default WgrShippingInfo;
